/** Identifies errors caused by a tab running a build that is no longer deployed.
 *
 * When a new version is released, the hashed lazy-chunk filenames change and the
 * old files are removed from the server. A long-lived tab still references the old
 * filenames, so the request falls through to the SPA rewrite and returns index.html.
 * The browser then rejects it, because HTML is not a valid module script.
 *
 * Browsers word this differently, hence the list of patterns below.
 */
const staleBuildErrorPatterns = [
	// Chromium / Firefox - dynamic import() rejection
	'failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	// Safari - dynamic import() rejection
	'importing a module script failed',
	// Strict MIME type checking rejecting the index.html fallback
	'failed to load module script',
	'expected a javascript-or-wasm module script',
	// Webpack style chunk loading (retained for older bundles)
	'loading chunk',
	'chunkloaderror',
];

/** Returns true if the error indicates the current tab is running a stale build
 * and can only recover by loading the page again. */
export const isStaleBuildError = (error: unknown): boolean => {
	if (!error) return false;

	if (typeof error === 'object' && (error as { name?: string }).name === 'ChunkLoadError') {
		return true;
	}

	const message = typeof error === 'string' ? error : ((error as { message?: string })?.message ?? '');

	if (!message) return false;

	const normalisedMessage = message.toLowerCase();

	return staleBuildErrorPatterns.some((pattern) => normalisedMessage.includes(pattern));
};

